// ajay:code 22-08-26
import { useState, useEffect } from "react";
import { Upload, Image as ImageIcon } from "lucide-react";
import { companyAPI } from "../../services/api";

export default function CompanySettings() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);

  const load = () => {
    setLoading(true);
    companyAPI
      .getMy()
      .then((r) => setCompany(r.data))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErr("Only image files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErr("Logo must be under 5MB");
      return;
    }
    setErr("");

    // Local preview before upload actually completes
    setPreviewUrl(URL.createObjectURL(file));

    setUploading(true);
    companyAPI
      .uploadLogo(file)
      .then(() => load())
      .catch((e) => setErr(e.message || "Upload failed"))
      .finally(() => setUploading(false));
  };

  if (loading) {
    return <div style={{ padding: 28, color: "var(--text2)" }}>Loading...</div>;
  }

  const logoSrc = previewUrl || company?.logo_url;

  return (
    <div style={{ padding: 28, maxWidth: 640 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>Company Settings</h1>
        <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
          {company?.name}
        </p>
      </div>

      {err && (
        <div
          style={{
            padding: "10px 16px",
            background: "rgba(255,77,106,0.1)",
            border: "1px solid var(--danger)",
            borderRadius: 8,
            color: "var(--danger)",
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {err}
        </div>
      )}

      <div className="card">
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
          Company Logo
        </h3>
        <p style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>
          Used across your PDIR / FPIR / PFMEA templates and reports.
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div
            style={{
              width: 120,
              height: 120,
              borderRadius: 10,
              background: "var(--surface2)",
              border: "1px solid var(--border2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="Company logo"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <ImageIcon size={32} color="var(--text3)" />
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label
              className="btn-primary"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: uploading ? "not-allowed" : "pointer",
                opacity: uploading ? 0.6 : 1,
                width: "fit-content",
              }}
            >
              <Upload size={14} />
              {uploading
                ? "Uploading..."
                : company?.logo_url
                  ? "Replace Logo"
                  : "Upload Logo"}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploading}
                style={{ display: "none" }}
              />
            </label>
            <span style={{ fontSize: 11, color: "var(--text3)" }}>
              PNG or JPG, up to 5MB. Square logos look best.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
